import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { Select, Tooltip, Typography } from 'antd'
import { Floor } from 'models/Building'
import { useRecoilState } from 'recoil'
import { floorDetailState, listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { FloorDetail, FloorMapElement, Point } from 'types/building'
import Utils from 'utils'

import Spinner from 'components/atoms/Spinner'
import DropdownIconCustom from 'components/icons/DropdownIconCustom'

import ViewerAreaDropdown from './ViewerAreaDropdown'
import ViewerFloorMap from './ViewerFloorMap'

type Props = {
  floors: Floor[]
}

export default function ViewerFloorDetail({ floors }: Props) {
  const { t } = useTranslation()
  const { id } = useParams()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setFloorDetail] = useRecoilState(floorDetailState)
  const [, setPoints] = useRecoilState(listPointState)
  const [, setSearchParams] = useSearchParams()

  const currentViewerRef = useRef(currentViewer)
  useEffect(() => {
    currentViewerRef.current = currentViewer
  }, [currentViewer])

  const {
    data: floorDetailData,
    isValidating: isValidatingFloor,
    mutate: refetchFloor
  } = useSWR<FloorDetail>(
    currentViewer?.selectedFloor
      ? {
          url: `v1/floors/${currentViewer?.selectedFloor}`
        }
      : null
  )

  const { data: floorPointsData, isValidating: isValidatingPoints } =
    useSWR<FloorMapElement>(
      currentViewer?.selectedFloor
        ? {
            url: 'v1/points',
            params: {
              buildingId: id,
              floorId: currentViewer?.selectedFloor
            }
          }
        : null
    )

  useEffect(() => {
    if (floorDetailData) {
      setFloorDetail(floorDetailData)
    }
  }, [floorDetailData, setFloorDetail])

  useEffect(() => {
    if (!floorPointsData) return

    try {
      const pointsList = floorPointsData.points || []
      setPoints(pointsList)

      const selectedPt = currentViewerRef.current.selectedPoint
      const currentPointInFloor = pointsList.find(
        (item) => item.id === selectedPt
      )

      if (currentPointInFloor) {
        const targetAreaId = currentPointInFloor.areaId
          ? `${currentPointInFloor.areaId}`
          : undefined
        setCurrentViewer((prev) => ({
          ...prev,
          selectedArea: targetAreaId
        }))

        const urlToUpdate = Utils.buildViewerUrl({
          area: targetAreaId || null
        })
        window.history.pushState({}, '', urlToUpdate.toString())
      } else {
        let defaultPoint: Point | null = null

        if (floorPointsData.defaultPoint) {
          defaultPoint =
            pointsList.find(
              (item) => item.id === floorPointsData.defaultPoint?.id
            ) || null
        }

        if (!defaultPoint) {
          const image360Points = pointsList.filter((item) => item.image360)
          defaultPoint =
            image360Points.length > 0
              ? image360Points.reduce((acc, curr) =>
                  curr.order < acc.order ? curr : acc
                )
              : pointsList[0] || null
        }

        if (defaultPoint) {
          const targetPoint = defaultPoint
          const targetAreaId = targetPoint.areaId
            ? `${targetPoint.areaId}`
            : undefined
          setCurrentViewer((prev) => ({
            ...prev,
            selectedArea: targetAreaId,
            selectedPoint: targetPoint.id,
            selectedImage: targetPoint.image360?.id,
            pointDetail: undefined
          }))

          const urlToUpdate = Utils.buildViewerUrl({
            area: targetAreaId || null
          })
          window.history.pushState({}, '', urlToUpdate.toString())
        } else {
          setCurrentViewer((prev) => ({
            ...prev,
            selectedArea: undefined,
            selectedPoint: undefined,
            selectedImage: undefined,
            pointDetail: undefined
          }))
        }
      }
    } catch (error) {
      setPoints([])
    }
  }, [floorPointsData, setCurrentViewer, setPoints])

  const listFloor = floors.map((floor) => {
    const isTitle = floor.name.length > 21 ? floor.name : undefined
    return {
      value: floor.id,
      label: (
        <Tooltip placement="right" title={isTitle}>
          <span className="text-[#021120] text-sm">
            {Utils.truncateString(floor.name, 21)}
          </span>
        </Tooltip>
      )
    }
  })

  const handleSelectFloor = (value: number) => {
    if (value === currentViewer?.selectedFloor) return

    setSearchParams((params) => {
      params.set('floor', String(value))
      params.delete('point')
      params.delete('area')
      params.delete('subimage')
      params.delete('hlookat')
      params.delete('vlookat')
      params.delete('fov')
      return params
    })

    setCurrentViewer((prev) => ({
      ...prev,
      selectedFloor: value,
      selectedArea: undefined,
      selectedImage: undefined,
      pointDetail: undefined,
      // Keep previous selectedPoint to prevent panorama from unmounting/resetting until new floor data arrives
      isSelectPointInLeftMenu: true,
      isClickToDirection: false,
      isClickToNextFloor: false
    }))
  }

  return (
    <>
      <div className="flex flex-col gap-2 p-[10px]">
        <Typography className="font-bold text-white">
          {t('common.sidebar.floor')}
        </Typography>
        <Select
          style={{
            width: 220,
            overflow: 'hidden'
          }}
          options={listFloor}
          // TODO: update default floor after BE update
          value={currentViewer?.selectedFloor}
          onSelect={handleSelectFloor}
          disabled={!listFloor.length}
          className={!listFloor.length ? 'select-floot-disabled' : ''}
          suffixIcon={<DropdownIconCustom colorIcon="#021120" />}
        />
      </div>

      {!isValidatingFloor && !isValidatingPoints ? (
        <>
          <ViewerFloorMap refetchFloor={refetchFloor} />

          <div className="h-full overflow-auto">
            <ViewerAreaDropdown />
          </div>
        </>
      ) : (
        <Spinner />
      )}
    </>
  )
}
